import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import ChatMessage from './components/ChatMessage';
import ChatInput, { Attachment } from './components/ChatInput';
import TopStrip from './components/TopStrip';
import ModulRail from './components/ModulRail';
import ThinkingIndicator from './components/ThinkingIndicator';
import ManagementPage from './pages/ManagementPage';
import DashboardsPage from './pages/DashboardsPage';
import LoginPage from './pages/LoginPage';
import RegistrierenPage from './pages/RegistrierenPage';
import PasswortVergessenPage from './pages/PasswortVergessenPage';
import SuperAdminPage from './pages/SuperAdminPage';
import WarnungenPanel from './components/WarnungenPanel';
import SplitWorkspace from './components/SplitWorkspace';
import CommandPalette from './components/CommandPalette';
import SchnellanfragenChips from './components/SchnellanfragenChips';
import SettingsDrawer from './components/settings/SettingsDrawer';
import InternetLeiste from './components/InternetLeiste';
import { Message, ChatResponse, Warnung, GithubEntwurf, BelegKarte } from './types';
import { AuthUser } from './types/auth';
import { issueNummerAusUrl } from './lib/githubEntwurf';
import { NAVIGIERE_EVENT, NavigiereDetail } from './lib/navigation';
import { VIEW_STORAGE_KEY, startView } from './lib/board';
import { TraceGroup } from './types/panel';
import './App.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

export type Ansicht = 'chat' | 'management' | 'superadmin' | 'dashboards';

/** benutzer_id aus dem JWT-Payload — Fallback für Sitzungen, deren
 *  gespeichertes auth_user noch keine benutzer_id enthält. */
function benutzerIdAusToken(token: string | null): number | undefined {
  if (!token) return undefined;
  try {
    const teil = token.split('.')[1];
    if (!teil) return undefined;
    const json = atob(teil.replace(/-/g, '+').replace(/_/g, '/'));
    const id = JSON.parse(json).benutzer_id;
    return typeof id === 'number' ? id : undefined;
  } catch {
    return undefined;
  }
}

/** Zuletzt gewählte Hauptansicht des Benutzers (Standard: Dashboards). */
function gespeicherteAnsicht(benutzerId: number | undefined): 'chat' | 'management' | 'dashboards' {
  if (benutzerId === undefined) return 'dashboards';
  try {
    return startView(localStorage.getItem(VIEW_STORAGE_KEY(benutzerId)));
  } catch {
    return 'dashboards';
  }
}

/** Ziel einer Navigation, die vor dem Mount der ManagementPage ausgelöst wurde. */
interface PendingNavigation {
  tab: string | null;
  params: Record<string, unknown>;
}

interface KonversationInfo {
  konversation_id: number;
  titel: string;
  erstellt_am: string;
  anzahl_nachrichten: number;
}

// Leerer Verlauf zeigt die Kontor-Hero-Begrüßung (k-hello) statt einer Pseudo-Nachricht

function App() {
  // Auth State – aus localStorage wiederherstellen
  const [authToken, setAuthToken] = useState<string | null>(
    () => localStorage.getItem('auth_token')
  );
  const [authUser, setAuthUser] = useState<AuthUser | null>(() => {
    const stored = localStorage.getItem('auth_user');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return {
      ...parsed,
      benutzer_id: parsed.benutzer_id ?? benutzerIdAusToken(localStorage.getItem('auth_token')),
      tenant_id: parsed.tenant_id ?? null,
      tenant_slug: parsed.tenant_slug ?? null,
      firmenname: parsed.firmenname ?? null,
      primaerfarbe: parsed.primaerfarbe ?? null,
    };
  });

  const [currentView, setCurrentView] = useState<Ansicht>(
    () => gespeicherteAnsicht(authUser?.benutzer_id)
  );
  const [managementTab, setManagementTab] = useState<string | undefined>(undefined);
  // Bühne offen? (steuert die is-staged Choreografie am k-app Root)
  const [staged, setStaged] = useState(false);
  // Zuletzt über die Modul-Rail geöffneter Verwaltungsbereich
  const [railTab, setRailTab] = useState<string | null>(null);
  // Stimmung (hell/dunkel/mix) — Umschalter im Top-Strip, persistiert
  const [mood, setMood] = useState<string>(() => localStorage.getItem('kontor-mood') ?? 'hell');
  const cycleMood = useCallback(() => {
    setMood(m => {
      const order = ['hell', 'dunkel', 'mix'];
      const next = order[(order.indexOf(m) + 1) % order.length];
      localStorage.setItem('kontor-mood', next);
      return next;
    });
  }, []);

  // Hauptansicht setzen und je Benutzer merken (Start nach Reload/Login)
  const benutzerId = authUser?.benutzer_id;
  const benutzerIdRef = useRef(benutzerId);
  benutzerIdRef.current = benutzerId;
  const setzeAnsicht = useCallback((view: Ansicht) => {
    setCurrentView(view);
    const id = benutzerIdRef.current;
    if (id === undefined) return;
    try {
      localStorage.setItem(VIEW_STORAGE_KEY(id), view);
    } catch { /* Speicher nicht verfügbar: Ansicht gilt nur für diese Sitzung */ }
  }, []);

  // Navigation per navigiere() (Dashboard-Kachel, Belegkarte im Chat, …): App
  // wechselt nur die Ansicht. Ist die ManagementPage schon gemountet, hört sie
  // das Ereignis selbst und wendet Tab + Parameter an; sonst reicht App beides
  // als initialTab/initialParams durch (pendingNavigation), damit nichts
  // doppelt angewendet wird.
  const [pendingNavigation, setPendingNavigation] = useState<PendingNavigation | null>(null);

  const handleViewChange = useCallback((view: Ansicht) => {
    setzeAnsicht(view);
    if (view !== 'management') setRailTab(null);
  }, [setzeAnsicht]);

  const handleRailNavigate = useCallback((tab: string) => {
    setPendingNavigation(null);
    setzeAnsicht('management');
    setManagementTab(tab);
    setRailTab(tab);
  }, [setzeAnsicht]);

  const handleCommandNavigate = useCallback((view: Ansicht, tab?: string) => {
    setPendingNavigation(null);
    setzeAnsicht(view);
    if (view === 'management' && tab) setManagementTab(tab);
    if (view !== 'management') setRailTab(null);
  }, [setzeAnsicht]);
  const handleTabConsumed = useCallback(() => {
    setManagementTab(undefined);
    setPendingNavigation(null);
  }, []);
  const currentViewRef = useRef(currentView);
  currentViewRef.current = currentView;
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<NavigiereDetail>).detail;
      if (!detail) return;
      if (detail.ansicht === 'einstellungen') {
        setSettingsSection(detail.tab ?? null);
        setSettingsOpen(true);
        return;
      }
      if (detail.ansicht === 'chat') {
        setRailTab(null);
        setzeAnsicht('chat');
        return;
      }
      if (detail.ansicht !== 'management') return;
      if (detail.tab) setRailTab(detail.tab);
      if (currentViewRef.current === 'management') return;
      setManagementTab(detail.tab ?? undefined);
      setPendingNavigation({ tab: detail.tab, params: detail.params ?? {} });
      setzeAnsicht('management');
    };
    window.addEventListener(NAVIGIERE_EVENT, handler);
    return () => window.removeEventListener(NAVIGIERE_EVENT, handler);
  }, [setzeAnsicht]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Gruppierter Tool-Trace (k-trace) + Statuszeile
  const [traceGroups, setTraceGroups] = useState<TraceGroup[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  // Laufzeit des abgeschlossenen Laufs (für die Einzeiler-Zusammenfassung)
  const [traceDuration, setTraceDuration] = useState(0);
  const handleThinking = useCallback((groups: TraceGroup[], active: boolean, durationSeconds?: number) => {
    setTraceGroups(groups);
    setIsThinking(active);
    if (durationSeconds !== undefined) setTraceDuration(durationSeconds);
  }, []);
  // Live-Reasoning (reasoning_delta): '' blendet die Zeile aus
  const [reasoningText, setReasoningText] = useState('');
  const handleReasoning = useCallback((text: string) => setReasoningText(text), []);
  // Finales Reasoning nach Abschluss („Gedacht" im aufgeklappten Trace)
  const [finalReasoning, setFinalReasoning] = useState('');
  const handleReasoningFinal = useCallback((text: string) => setFinalReasoning(text), []);
  // Abbruch-Funktion des laufenden Streams (von SplitWorkspace bereitgestellt)
  const [abortFn, setAbortFn] = useState<(() => void) | null>(null);
  const handleAbortReady = useCallback((fn: () => void) => setAbortFn(() => fn), []);
  // Bühnen-Tab aus einer Nachricht heraus öffnen
  const [openStageFn, setOpenStageFn] = useState<((ref: string) => void) | null>(null);
  const handleOpenStageReady = useCallback(
    (fn: (ref: string) => void) => setOpenStageFn(() => fn),
    []
  );
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [konversationen, setKonversationen] = useState<KonversationInfo[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Token-Streaming: Id der gerade live wachsenden Assistant-Nachricht (oder null)
  const streamingIdRef = useRef<string | null>(null);

  // Warnungen State
  const [warnungen, setWarnungen] = useState<Warnung[]>([]);
  const [showWarnungen, setShowWarnungen] = useState(false);

  // SSE Streaming sender (provided by SplitWorkspace via onReady)
  const [streamSend, setStreamSend] = useState<((c: string, attachments?: Attachment[]) => Promise<void>) | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Sektion, die der Drawer beim Öffnen zeigen soll (Board-Ziel „einstellungen“)
  const [settingsSection, setSettingsSection] = useState<string | null>(null);

  const handleStreamReady = useCallback(
    (fn: (c: string, attachments?: Attachment[]) => Promise<void>) => setStreamSend(() => fn),
    []
  );

  // Stabile Identität: SplitWorkspace leitet diese Callbacks über useCallback-
  // Ketten in den onReady-Effekt; inline-Arrows würden bei jedem Render eine
  // neue sendMessage-Identität erzeugen → Endlos-Render-Loop (Browser-OOM).
  // Token-Streaming: laufende Antwort live aktualisieren. fullText ist der
  // gesamte bisher gestreamte Text des aktuellen Turns (kann '' sein = Reset).
  const handleAssistantDelta = useCallback((fullText: string) => {
    if (streamingIdRef.current === null) {
      if (!fullText) return; // leeres Reset ohne laufende Nachricht: nichts tun
      const id = `stream-${Date.now()}`;
      streamingIdRef.current = id;
      setMessages(prev => [...prev, {
        id, role: 'assistant', content: fullText, timestamp: new Date(),
      }]);
      return;
    }
    const id = streamingIdRef.current;
    setMessages(prev => prev.map(m => (m.id === id ? { ...m, content: fullText } : m)));
  }, []);

  const handleAssistantMessage = useCallback(
    (text: string, agent?: string, panel?: { ref: string; title: string }) => {
      // Lief ein Stream? Dann die gewachsene Nachricht autoritativ finalisieren
      // statt eine zweite anzuhängen.
      if (streamingIdRef.current !== null) {
        const id = streamingIdRef.current;
        streamingIdRef.current = null;
        setMessages(prev => prev.map(m => (
          m.id === id
            ? { ...m, content: text, agent, panelRef: panel?.ref, panelTitle: panel?.title }
            : m
        )));
        return;
      }
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(), role: 'assistant',
        content: text, timestamp: new Date(), agent,
        panelRef: panel?.ref, panelTitle: panel?.title
      };
      setMessages(prev => [...prev, assistantMessage]);
    },
    []
  );

  // GitHub-Ticket-Entwurf: eigene Assistant-Nachricht (Karte), sofort angehängt.
  // streamingIdRef bleibt unberührt — der Antworttext wächst weiter in seiner
  // eigenen Nachricht und wird per Id aktualisiert, nie mit der Karte vermischt.
  const handleGithubEntwurf = useCallback((entwurf: GithubEntwurf) => {
    setMessages(prev => [...prev, {
      id: `ghentwurf-${entwurf.entwurf_id}-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      githubEntwurf: entwurf,
    }]);
  }, []);

  // Endzustand der Karte (angelegt/verworfen) in der Nachrichtenliste festhalten
  // Nach dem Anlegen zusätzlich dieselbe Zeile zeigen, die das Backend als
  // Systemnachricht im Verlauf persistiert (bleibt nach Reload sichtbar).
  const handleGithubEntwurfChange = useCallback((id: string, entwurf: GithubEntwurf) => {
    setMessages(prev => {
      const aktualisiert = prev.map(m => (m.id === id ? { ...m, githubEntwurf: entwurf } : m));
      if (entwurf.status !== 'ANGELEGT' || !entwurf.issue_url) return aktualisiert;
      const nr = issueNummerAusUrl(entwurf.issue_url);
      return [...aktualisiert, {
        id: `ghticket-${entwurf.entwurf_id}-${Date.now()}`,
        role: 'assistant' as const,
        content: `GitHub-Ticket${nr !== null ? ` #${nr}` : ''} angelegt: ${entwurf.issue_url}`,
        timestamp: new Date(),
        agent: 'System',
      }];
    });
  }, []);

  // Belegkarte des Agenten: wie der Ticket-Entwurf eine eigene Assistant-Nachricht
  // (die Antwort streamt in ihrer eigenen Nachricht weiter). Nach einem Reload kommt
  // die Karte über `karten` der persistierten Antwort wieder.
  const handleBelegKarte = useCallback((karte: BelegKarte) => {
    setMessages(prev => [...prev, {
      id: `belegkarte-${karte.typ}-${karte.id}-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      belegKarten: [karte],
    }]);
  }, []);

  // Neuer Kartenstand nach einer Aktion (gesendet/gebucht/…) in der Nachricht festhalten
  const handleBelegKarteChange = useCallback((id: string, karte: BelegKarte) => {
    setMessages(prev => prev.map(m => (m.id === id && m.belegKarten
      ? { ...m, belegKarten: m.belegKarten.map(k => (k.typ === karte.typ && k.id === karte.id ? karte : k)) }
      : m)));
  }, []);

  const handleStreamError = useCallback((msg: string) => {
    streamingIdRef.current = null;
    const errorMessage: Message = {
      id: (Date.now() + 1).toString(), role: 'assistant',
      content: `Fehler: ${msg}`, timestamp: new Date()
    };
    setMessages(prev => [...prev, errorMessage]);
  }, []);

  // Stop-Button: Stream abbrechen und die teilweise gestreamte Nachricht als
  // abgeschlossen behandeln — sonst würde die NÄCHSTE Antwort in die alte
  // Nachricht hineinwachsen (streamingIdRef zeigt noch auf sie).
  const handleAbort = useCallback(() => {
    abortFn?.();
    streamingIdRef.current = null;
  }, [abortFn]);

  // Sekundenzähler für die Statuszeile: läuft solange der Agent arbeitet
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const busy = isThinking || isLoading;

  // Statuszeile: aktiver Agent = letzte running-Gruppe (Fallback "Agent"),
  // letzter Step bevorzugt aus dieser Gruppe, sonst letzter Step überhaupt
  const runningGroup = [...traceGroups].reverse().find(g => g.status === 'running');
  const activeAgentName = runningGroup?.agent ?? 'Agent';
  let lastTraceStep = '';
  if (runningGroup && runningGroup.steps.length > 0) {
    lastTraceStep = runningGroup.steps[runningGroup.steps.length - 1];
  } else {
    for (let i = traceGroups.length - 1; i >= 0; i--) {
      const g = traceGroups[i];
      if (g.steps.length > 0) { lastTraceStep = g.steps[g.steps.length - 1]; break; }
    }
  }
  useEffect(() => {
    if (!busy) return;
    setElapsedSeconds(0);
    const interval = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, [busy]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, traceGroups]);

  // Globaler Drop-Schutz: eine neben einer Dropzone fallengelassene Datei
  // würde der Browser sonst als Navigation öffnen und die Seite ersetzen.
  useEffect(() => {
    const verhindereStandardDrop = (e: globalThis.DragEvent) => {
      e.preventDefault();
    };
    window.addEventListener('dragover', verhindereStandardDrop);
    window.addEventListener('drop', verhindereStandardDrop);
    return () => {
      window.removeEventListener('dragover', verhindereStandardDrop);
      window.removeEventListener('drop', verhindereStandardDrop);
    };
  }, []);

  // Tenant-Primärfarbe wird bewusst NICHT mehr ins Layout injiziert:
  // Das Kontor-Design erlaubt ausschließlich Token-Farben (siehe Migrationsplan).

  // Login handler
  const handleLogin = (token: string, user: AuthUser) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(user));
    setAuthToken(token);
    setAuthUser(user);
    setCurrentView(gespeicherteAnsicht(user.benutzer_id));
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setAuthToken(null);
    setAuthUser(null);
    setConversationId(null);
    setMessages([]);
    setWarnungen([]);
  };

  // Warnungen laden (Polling alle 60s)
  const loadWarnungen = useCallback(async () => {
    try {
      const res = await axios.get<Warnung[]>(`${API_URL}/api/warnungen?nur_ungelesen=true`);
      setWarnungen(res.data);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    if (!authToken) return;
    loadWarnungen();
    const interval = setInterval(loadWarnungen, 60_000);
    return () => clearInterval(interval);
  }, [authToken, loadWarnungen]);

  const handleMarkRead = async (id: number) => {
    try {
      await axios.put(`${API_URL}/api/warnungen/${id}/lesen`);
      setWarnungen(prev => prev.filter(w => w.warnung_id !== id));
    } catch { /* ignore */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API_URL}/api/warnungen/alle-lesen`);
      setWarnungen([]);
    } catch { /* ignore */ }
  };

  // Konversationsliste laden
  const loadKonversationen = useCallback(async () => {
    try {
      const res = await axios.get<KonversationInfo[]>(`${API_URL}/api/konversationen?limit=15`);
      setKonversationen(res.data);
    } catch {
      // Silently fail – History ist optional
    }
  }, []);

  useEffect(() => {
    if (showHistory) loadKonversationen();
  }, [showHistory, loadKonversationen]);

  // Bestehende Konversation laden
  const loadKonversation = async (id: number) => {
    try {
      const res = await axios.get(`${API_URL}/api/konversationen/${id}`);
      const nachrichten: Message[] = res.data.nachrichten.map((n: any) => ({
        id: n.nachricht_id.toString(),
        role: n.rolle as 'user' | 'assistant',
        content: n.inhalt,
        timestamp: new Date(n.erstellt_am),
        agent: n.agent_genutzt,
        // Belegkarten (serverseitig frisch aufgelöst) wieder an der Nachricht zeigen
        belegKarten: Array.isArray(n.karten) && n.karten.length > 0 ? n.karten as BelegKarte[] : undefined,
      }));
      setMessages(nachrichten);
      setConversationId(id);
      setShowHistory(false);
    } catch {
      console.error('Fehler beim Laden der Konversation');
    }
  };

  // Neue Konversation starten
  const startNewConversation = () => {
    setConversationId(null);
    setMessages([]);
    setShowHistory(false);
  };

  const handleSendMessage = async (content: string) => {
    setIsLoading(true);

    try {
      const response = await axios.post<ChatResponse>(`${API_URL}/api/chat`, {
        message: content,
        conversation_id: conversationId,
        user_role: authUser?.rolle
      });

      if (response.data.conversation_id && !conversationId) {
        setConversationId(response.data.conversation_id);
      }

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(),
        agent: response.data.agent_used
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Fehler beim Senden:', error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Entschuldigung, es ist ein Fehler aufgetreten. Bitte stelle sicher, dass der Backend-Server läuft und die Anthropic API Key konfiguriert ist.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  // Public Route: Einladungs-Link einlösen (kein Login nötig)
  if (window.location.pathname === '/registrieren') {
    return <RegistrierenPage />;
  }

  // Public Route: Passwort-Reset anfordern (kein Login nötig)
  if (window.location.pathname === '/passwort-vergessen') {
    return <PasswortVergessenPage />;
  }

  // Nicht eingeloggt → LoginPage
  if (!authToken || !authUser) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div
      className={'k-app' + (currentView === 'chat' && staged ? ' is-staged' : '')}
      data-mood={mood}
      data-anim="inszeniert"
      data-density="luftig"
    >
      <CommandPalette onNavigate={handleCommandNavigate} />
      <TopStrip
        currentView={currentView}
        onViewChange={handleViewChange}
        user={authUser}
        onLogout={handleLogout}
        warnungenAnzahl={warnungen.length}
        onWarnungenClick={() => setShowWarnungen(v => !v)}
        onSettingsClick={() => { setSettingsSection(null); setSettingsOpen(true); }}
        mood={mood}
        onMoodToggle={cycleMood}
      />
      <SettingsDrawer
        open={settingsOpen}
        initialSection={settingsSection}
        onClose={() => setSettingsOpen(false)}
        authToken={authToken ?? ''}
        tenantId={authUser?.tenant_id ?? null}
        userRolle={authUser?.rolle ?? ''}
        authUser={authUser}
        onOpenSuperAdmin={() => { setSettingsOpen(false); setCurrentView('superadmin'); }}
      />

      {showWarnungen && (
        <WarnungenPanel
          warnungen={warnungen}
          onClose={() => setShowWarnungen(false)}
          onMarkRead={handleMarkRead}
          onMarkAllRead={handleMarkAllRead}
        />
      )}

      <div className="k-main">
        <ModulRail activeTab={currentView === 'management' ? railTab : null} onNavigate={handleRailNavigate} />

        {currentView === 'superadmin' && authUser.rolle === 'SuperAdmin' ? (
          <SuperAdminPage authUser={authUser} authToken={authToken || ''} />
        ) : currentView === 'chat' ? (
          <SplitWorkspace
            authToken={authToken ?? ''}
            conversationId={conversationId}
            onConversationId={setConversationId}
            onStagedChange={setStaged}
            onAssistantMessage={handleAssistantMessage}
            onAssistantDelta={handleAssistantDelta}
            onThinking={handleThinking}
            onReasoning={handleReasoning}
            onReasoningFinal={handleReasoningFinal}
            onOpenStageReady={handleOpenStageReady}
            onAbortReady={handleAbortReady}
            onError={handleStreamError}
            onGithubEntwurf={handleGithubEntwurf}
            onBelegKarte={handleBelegKarte}
            onLoadingChange={setIsLoading}
            onReady={handleStreamReady}
          >
            <div className="chat-container">
              <div className="chat-toolbar">
                <button className="toolbar-btn new-chat-btn" onClick={startNewConversation}>
                  + Neue Konversation
                </button>
                <button
                  className={`toolbar-btn history-btn ${showHistory ? 'active' : ''}`}
                  onClick={() => setShowHistory(v => !v)}
                >
                  Verlauf {conversationId ? `(#${conversationId})` : ''}
                </button>
              </div>

              {showHistory && (
                <div className="history-panel">
                  <div className="history-header">
                    <span>Letzte Konversationen</span>
                    <button className="history-close" onClick={() => setShowHistory(false)}>✕</button>
                  </div>
                  {konversationen.length === 0 ? (
                    <p className="history-empty">Noch keine gespeicherten Konversationen</p>
                  ) : (
                    <ul className="history-list">
                      {konversationen.map(k => (
                        <li
                          key={k.konversation_id}
                          className={`history-item ${k.konversation_id === conversationId ? 'active' : ''}`}
                          onClick={() => loadKonversation(k.konversation_id)}
                        >
                          <span className="history-titel">{k.titel || 'Unbenannte Konversation'}</span>
                          <span className="history-meta">{formatDate(k.erstellt_am)} · {k.anzahl_nachrichten} Nachrichten</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {messages.length === 0 && !isThinking ? (
                <div className="k-hello">
                  <span className="k-hello__eyebrow">Kontor / Agentisches ERP</span>
                  <h1 className="k-hello__h1">Fragen Sie Ihr <em>Unternehmen</em>.</h1>
                  <p className="k-hello__lede">
                    Keine Masken, keine Menüpfade. Der Agent liest Ihre ERP-Daten,
                    rechnet, und legt Ihnen das Ergebnis <strong>auf die Bühne</strong> —
                    jede Aktion nach außen — Angebot, Rechnung, Zahlung — wartet auf Ihren Klick.
                  </p>
                </div>
              ) : (
                <div className="k-msgs messages">
                  {messages.map(message => (
                    <ChatMessage
                      key={message.id}
                      message={message}
                      onOpenStage={openStageFn ?? undefined}
                      stageOpenRef={null}
                      onGithubEntwurfChange={handleGithubEntwurfChange}
                      onBelegKarteChange={handleBelegKarteChange}
                    />
                  ))}
                  {(isThinking || traceGroups.length > 0) && (
                    <ThinkingIndicator
                      groups={traceGroups}
                      isActive={isThinking}
                      reasoningText={reasoningText}
                      finalReasoning={finalReasoning}
                      durationSeconds={traceDuration}
                    />
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}

              <div className="k-composer">
                <SchnellanfragenChips
                  authToken={authToken ?? ''}
                  onScenarioClick={(text) => {
                    const userMsg: Message = { id: Date.now().toString(), role: 'user', content: text, timestamp: new Date() }
                    setMessages(prev => [...prev, userMsg])
                    if (streamSend) {
                      streamSend(text)
                    } else {
                      handleSendMessage(text)
                    }
                  }}
                />
                <ChatInput
                  onSendMessage={(content, attachments) => {
                    const userMsg: Message = { id: Date.now().toString(), role: 'user', content, timestamp: new Date() }
                    setMessages(prev => [...prev, userMsg])
                    if (streamSend) {
                      streamSend(content, attachments)
                    } else {
                      handleSendMessage(content)
                    }
                  }}
                  disabled={isLoading}
                  onAbort={abortFn ? handleAbort : undefined}
                />
                <InternetLeiste istAdmin={authUser?.rolle === 'Admin'} />
                <div className={'k-status' + (busy ? ' is-busy' : '')}>
                  <span className="k-status__dot"></span>
                  <span className="k-status__text">
                    {busy
                      ? `${activeAgentName} arbeitet (${elapsedSeconds} s)`
                        + (isThinking && lastTraceStep ? ` — ${lastTraceStep}` : '')
                      : 'Agent bereit'}
                  </span>
                </div>
              </div>
            </div>
          </SplitWorkspace>
        ) : currentView === 'dashboards' ? (
          <DashboardsPage authUser={authUser} />
        ) : (
          <ManagementPage
            userRolle={authUser.rolle}
            authToken={authToken || ''}
            authUser={authUser}
            initialTab={managementTab}
            initialParams={pendingNavigation?.params}
            onInitialTabConsumed={handleTabConsumed}
          />
        )}
      </div>
    </div>
  );
}

export default App;
